import {
  Button,
  TextField,
  MenuItem,
  Box,
  DialogActions,
  DialogContent,
  Stack,
  FormControlLabel,
  Switch,
  DialogTitle,
  FormHelperText,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useAuthUser } from "react-auth-kit";
import { renderDeviceName } from "../../../utils/utils";
import { useNewDevice, devices } from "..";
import { useGetPatients } from "../../PatientComponents";
import { useGetOrganizations } from "../../HandlingComponents";
import { useGetKits } from "../../KitComponents";
import { prettify } from "../../../utils/utils";
import {
  buildDevicePayload,
  isAssociationValueSet,
  validateDeviceAssociations,
} from "../deviceFormUtils";

const validationSchema = yup
  .object({
    name: yup.string(),
    device_type: yup.number().nullable(),
    is_installed: yup.boolean(),
    is_online: yup.boolean(),
    serial_number: yup
      .string()
      .trim()
      .required("Numero seriale obbligatorio"),
    note: yup.string(),
    org_id: yup.mixed().nullable(),
    patient_id: yup.mixed().nullable(),
    kit_id: yup.mixed().nullable(),
  })
  .test("association-required", function (values) {
    const { association } = validateDeviceAssociations(values);
    if (!association) return true;

    return this.createError({
      path: "association",
      message: association,
    });
  });

function AddDevice({ closeModal }) {
  const authUser = useAuthUser();
  const newDeviceMutation = useNewDevice(closeModal);
  const patientsQuery = useGetPatients();
  const organizationsQuery = useGetOrganizations();
  const kitsQuery = useGetKits();

  const patients = patientsQuery.data?.patients ?? [];
  const organizations = organizationsQuery.data?.organizations ?? [];
  const kits = kitsQuery.data?.kits ?? [];

  const formik = useFormik({
    initialValues: {
      name: "",
      device_type: null,
      is_installed: false,
      is_online: false,
      serial_number: "",
      note: "",
      patient_id: "",
      org_id: null,
      kit_id: "",
    },
    validationSchema,
    onSubmit: (values) => {
      newDeviceMutation.mutate(buildDevicePayload(values, authUser));
    },
  });

  const effectiveOrgId =
    authUser()?.role === 1
      ? formik.values.org_id === "" || formik.values.org_id == null
        ? null
        : Number(formik.values.org_id)
      : authUser()?.user?.org_id ?? null;

  const filteredPatients = effectiveOrgId
    ? patients.filter((p) => Number(p.org_id) === Number(effectiveOrgId))
    : patients;

  const filteredKits = effectiveOrgId
    ? kits.filter((k) => Number(k.org_id) === Number(effectiveOrgId))
    : kits;

  const handleOrgChange = (event) => {
    const orgId =
      event.target.value === "" ? null : Number(event.target.value);
    formik.setFieldValue("org_id", orgId);

    if (formik.values.patient_id !== "") {
      const patient = patients.find((p) => p.id === formik.values.patient_id);
      if (patient && patient.org_id !== orgId) {
        formik.setFieldValue("patient_id", "");
      }
    }
    if (formik.values.kit_id !== "") {
      const kit = kits.find((k) => k.id === Number(formik.values.kit_id));
      if (kit && orgId != null && kit.org_id !== orgId) {
        formik.setFieldValue("kit_id", "");
      }
    }
  };

  const handlePatientChange = (event) => {
    const patientId =
      event.target.value === "" ? "" : Number(event.target.value);
    formik.setFieldValue("patient_id", patientId);

    if (patientId !== "") {
      formik.setFieldValue("kit_id", "");
    }

    if (authUser()?.role === 1 && patientId !== "") {
      const patient = patients.find((p) => p.id === patientId);
      if (patient?.org_id != null) {
        formik.setFieldValue("org_id", patient.org_id);
      }
    }
  };

  const handleKitChange = (event) => {
    const kitId = event.target.value === "" ? "" : event.target.value;
    formik.setFieldValue("kit_id", kitId);

    if (kitId !== "") {
      formik.setFieldValue("patient_id", "");
    }

    if (authUser()?.role === 1 && kitId !== "") {
      const kit = kits.find((k) => k.id === Number(kitId));
      if (kit?.org_id != null) {
        formik.setFieldValue("org_id", kit.org_id);
      }
    }
  };

  const showAssociationError =
    Boolean(formik.errors.association) && formik.submitCount > 0;

  return (
    <>
      <form noValidate onSubmit={formik.handleSubmit}>
        <DialogTitle>Nuovo dispositivo</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={8} sx={{ width: "100%" }}>
            <Box sx={{ float: "left", width: "50%" }}>
              <TextField
                autoFocus
                margin="dense"
                fullWidth
                id="name"
                name="name"
                label="Dispositivo"
                type="text"
                variant="standard"
                onChange={formik.handleChange}
                value={formik.values.name}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />

              <TextField
                autoFocus
                select
                margin="dense"
                fullWidth
                id="device_type"
                name="device_type"
                label="Tipo di dispositivo"
                type="text"
                variant="standard"
                onChange={formik.handleChange}
                value={formik.values.device_type}
                error={
                  formik.touched.device_type &&
                  Boolean(formik.errors.device_type)
                }
                helperText={
                  formik.touched.device_type && formik.errors.device_type
                }
              >
                {devices.map((option) => (
                  <MenuItem key={option.device_type} value={option.device_type}>
                    {renderDeviceName(option.device_type)}
                  </MenuItem>
                ))}
              </TextField>

              <FormHelperText sx={{ mt: 1, mb: 0 }}>
                Associazione: organizzazione, paziente o kit
              </FormHelperText>

              {authUser()?.role === 1 && organizationsQuery.isSuccess && (
                <TextField
                  select
                  margin="dense"
                  fullWidth
                  id="org_id"
                  name="org_id"
                  label="Organizzazione"
                  variant="standard"
                  onChange={handleOrgChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.org_id ?? ""}
                  error={showAssociationError}
                >
                  <MenuItem value="">
                    <em>Seleziona organizzazione</em>
                  </MenuItem>
                  {organizations.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <TextField
                select
                margin="dense"
                fullWidth
                id="patient_id"
                name="patient_id"
                label="Paziente"
                variant="standard"
                onChange={handlePatientChange}
                onBlur={formik.handleBlur}
                value={formik.values.patient_id}
                error={showAssociationError}
              >
                <MenuItem value="">
                  <em>Nessun paziente</em>
                </MenuItem>
                {filteredPatients.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {`${option.first_name} ${option.last_name}`}
                  </MenuItem>
                ))}
              </TextField>

              {kitsQuery.isSuccess && (
                <TextField
                  select
                  margin="dense"
                  fullWidth
                  id="kit_id"
                  name="kit_id"
                  label="Kit"
                  variant="standard"
                  onChange={handleKitChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.kit_id}
                  error={showAssociationError}
                >
                  <MenuItem value="">
                    <em>Nessun kit</em>
                  </MenuItem>
                  {filteredKits.map((kit) => (
                    <MenuItem key={kit.id} value={kit.id}>
                      {prettify(kit.name)}
                      {kit.patient_info && kit.patient_info !== "Non associato"
                        ? ` (${kit.patient_info})`
                        : ""}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {showAssociationError && (
                <FormHelperText error sx={{ mt: 0 }}>
                  {formik.errors.association}
                </FormHelperText>
              )}

              <TextField
                autoFocus
                margin="dense"
                fullWidth
                id="serial_number"
                name="serial_number"
                label="Numero Seriale / Mac Address"
                type="text"
                variant="standard"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.serial_number}
                error={
                  formik.touched.serial_number &&
                  Boolean(formik.errors.serial_number)
                }
                helperText={
                  formik.touched.serial_number && formik.errors.serial_number
                }
              />
            </Box>

            <Box sx={{ float: "right", width: "50%" }}>
              <FormControlLabel
                control={
                  <Switch
                    name="is_installed"
                    value={formik.values.is_installed}
                    checked={formik.values.is_installed}
                    onChange={formik.handleChange}
                  />
                }
                label="Installato"
                labelPlacement="start"
              />
              <FormControlLabel
                control={
                  <Switch
                    name="is_online"
                    value={formik.values.is_online}
                    checked={formik.values.is_online}
                    onChange={formik.handleChange}
                  />
                }
                label="Online"
                labelPlacement="start"
              />

              <TextField
                margin="dense"
                fullWidth
                id="note"
                name="note"
                label="Note"
                multiline
                minRows={4}
                type="text"
                variant="standard"
                onChange={formik.handleChange}
                value={formik.values.note}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={newDeviceMutation.isLoading} type="submit">
            Conferma
          </Button>
          <Button
            disabled={newDeviceMutation.isLoading}
            onClick={formik.handleReset}
          >
            Reset
          </Button>
          <Button
            disabled={newDeviceMutation.isLoading}
            color="error"
            onClick={closeModal}
          >
            Annulla
          </Button>
        </DialogActions>
      </form>
    </>
  );
}

export default AddDevice;
